import { TExtractedNamePart, TKycWithDocumentNamesRow } from '@verifime/components';
import { TIdentityDocumentType } from '@verifime/utils';

/**
 * A driver licence, holding only the fields this table reads.
 *
 * See `TCustomerDocuments` for why each shape is declared here rather than imported.
 */
export type TKycDriverLicence = {
  licenceCountryOfIssue?: string;
  licenceExpiry?: string;
  licenceFamilyName?: string;
  licenceFirstName?: string;
  licenceMiddleName?: string;
  licenceName?: string;
};

/**
 * A photo card, holding only the fields this table reads.
 *
 * The card number and the state are on the response and are left out on purpose: no row shows
 * either, and a type that does not name the number cannot be read for it by accident.
 */
export type TKycPhotoCard = {
  photoCardCountryOfIssue?: string;
  photoCardExpiry?: string;
  photoCardFamilyName?: string;
  photoCardFirstName?: string;
  photoCardMiddleName?: string;
  photoCardName?: string;
};

/** A passport, holding only the fields this table reads. */
export type TKycPassport = {
  passportCountryOfIssue?: string;
  passportExpiry?: string;
  passportFamilyName?: string;
  passportGivenNames?: string;
  passportName?: string;
};

/**
 * An ImmiCard, holding only the fields this table reads.
 *
 * The card number and the status are on the response and are left out on purpose: no row shows
 * either, and a type that does not name them cannot be read for them by accident. There is no
 * country of issue, because the response has no such field for an ImmiCard.
 */
export type TKycImmiCard = {
  immiCardExpiry?: string;
  immiCardFamilyName?: string;
  immiCardGivenNames?: string;
  immiCardName?: string;
};

/** A Medicare card, holding only the fields this table reads. */
export type TKycMedicareCard = {
  medicareCardExpiry?: string;
  medicareCardFullName?: string;
};

/** An other identity document, holding only the fields this table reads. */
export type TKycOtherIdentityDocument = {
  countryOfIssue?: string;
  dateOfExpiry?: string;
  otherDocumentType?: string;
};

/**
 * The identity documents a customer holds, named as the customer response names them.
 *
 * Each is its own shape rather than a common document type, because the fields genuinely differ:
 * a licence and a photo card each have first, middle and family names (the table prints each as one
 * joined name), a passport and an ImmiCard have two name parts (the ImmiCard with no country of
 * issue), a Medicare card has one whole name and no country of issue at all, and an other identity
 * document has no name fields.
 *
 * **Declared here as its own structural shape rather than imported from a generated API type.**
 * This is the views layer, so depending on the API contract is expected (ADR 50/62); what this
 * type avoids is depending on one particular *generator's* rendering of that contract.
 * `ConfidenceCustomerPage`'s `identityDocumentsOf` casts a Pont-generated customer into this shape
 * because Pont's interfaces carry no index signature and this type's fields, all optional and
 * named exactly as the response names them, accept it structurally with no mapping. A caller on
 * Zodios satisfies the same shape for the same reason. Importing the Zodios-generated interface
 * instead would fix that flexibility to one generator and reintroduce a conversion step the other
 * caller does not need.
 *
 * It also keeps a rename from silently redefining what this table reads. Importing a generated type
 * would let an upstream field rename quietly change what `documentRows` picks up, and the column
 * would just stop being populated. Declared here, a renamed field means the API object no longer
 * satisfies the shape and the mapping below stops compiling, in the same file that decides what to
 * do about it.
 */
export type TCustomerDocuments = {
  driverLicence?: TKycDriverLicence;
  photoCard?: TKycPhotoCard;
  passport?: TKycPassport;
  immiCard?: TKycImmiCard;
  medicareCard?: TKycMedicareCard;
  otherIdentityDocument?: TKycOtherIdentityDocument;
};

/**
 * Builds the name parts for one document, dropping any the document did not print.
 *
 * **A missing part is left out, not shown blank.** An empty value row reads as "the document says
 * this part is nothing", which is a different claim from "this document does not have that part".
 * A licence with no middle name prints two parts, and that is what the column should show.
 */
function nameParts(parts: Array<[string, string | undefined]>): TExtractedNamePart[] | undefined {
  const printed = parts
    .filter(([, value]) => Boolean(value))
    .map(([label, value]) => ({ label, value: value as string }));

  return printed.length > 0 ? printed : undefined;
}

/**
 * Prefers a document's structured name parts, falling back to its legacy whole-name field only when
 * no structured part is present at all.
 *
 * **Any structured part present wins outright, even an incomplete set.** A passport's given names
 * with no family name printed is still a structured name, not a partial one to abandon:
 * `nameParts` already leaves the missing part out rather than showing it blank, and that is the
 * printed name. Falling back "whenever the structured set is incomplete" would treat a legitimate
 * partial response as untrustworthy, which it is not. The legacy field only takes over for a
 * record that carries no structured field whatsoever, e.g. a document scanned before those fields
 * existed.
 *
 * The fallback prints under the label `'Name'`, matching the single whole-name label a Medicare
 * card already uses, so the column reads the same way regardless of which shape produced it.
 *
 * **Still used for the passport and the ImmiCard, which genuinely print two separate parts.** The
 * driver licence used to go through here too, keeping the structured three-part shape apart from
 * the legacy whole-name shape. It no longer does: see `driverLicenceName` below.
 */
function structuredOrLegacyName(
  structuredParts: Array<[string, string | undefined]>,
  legacyName: string | undefined,
): TExtractedNamePart[] | undefined {
  return nameParts(structuredParts) ?? nameParts([['Name', legacyName]]);
}

/**
 * The words the ImmiCard row prints, in one place.
 *
 * The labels match the passport's name parts, because the card prints the same two.
 */
const IMMI_CARD_COPY = {
  // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
  documentType: 'ImmiCard',
  // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
  givenNames: 'Given names',
  // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
  familyName: 'Family name',
} as const;

/**
 * A driver licence holder's name, as a single value under the label `'Name'`.
 *
 * A licence still carries three separate name fields, because the government DVS check needs them
 * separately. But those fields are not read off the card: they are the customer's own page-1
 * answers, deliberately preferred over the licence's OCR reading because OCR is unreliable.
 * Presenting them as three document-derived parts implied a validation against the document that
 * never actually ran. That is true regardless of what DVS itself checks: today DVS still receives
 * and checks all three parts. person-api#1845 puts the middle name behind a
 * `driver-licence-middle-name-suppression.enabled` toggle, defaulted off, so DVS keeps receiving
 * it until that toggle is enabled. The label was the thing that was inaccurate, not the data, so
 * this joins the same three answers into the one value a client actually reads: what the customer
 * said their name is.
 * The middle name, when the customer gave one, stays inside that joined value: clients want it on
 * the record.
 *
 * **Collapses the structured-versus-legacy distinction `structuredOrLegacyName` still keeps for the
 * passport.** Once every driver licence name prints as one `'Name'` pair, the structured and legacy
 * paths produce the identical shape, so there is no longer a second shape to keep apart: the join
 * either produces a non-empty value, or it does not and the legacy field takes over.
 */
function driverLicenceName(driverLicence: TKycDriverLicence): TExtractedNamePart[] | undefined {
  const joined = [
    driverLicence.licenceFirstName,
    driverLicence.licenceMiddleName,
    driverLicence.licenceFamilyName,
  ]
    .filter(Boolean)
    .join(' ');

  return nameParts([['Name', joined]]) ?? nameParts([['Name', driverLicence.licenceName]]);
}

/**
 * A photo card holder's name, as a single value under the label `'Name'`, the way
 * `driverLicenceName` prints a licence holder's.
 *
 * **The whole-name fallback is the ordinary path for some cards, not a legacy one.** Some issuers
 * print a full name only, so the scan leaves the first and family names empty and `photoCardName`
 * is all there is. The structured parts still win whenever any is present, as they do for a licence.
 */
function photoCardName(photoCard: TKycPhotoCard): TExtractedNamePart[] | undefined {
  const joined = [
    photoCard.photoCardFirstName,
    photoCard.photoCardMiddleName,
    photoCard.photoCardFamilyName,
  ]
    .filter(Boolean)
    .join(' ');

  return nameParts([['Name', joined]]) ?? nameParts([['Name', photoCard.photoCardName]]);
}

/**
 * The document types in the order a client reads them.
 *
 * Fixed here rather than derived from the response's key order, which would make what a client sees
 * depend on how the API happened to serialise the object. The grid does not sort, so this order is
 * the order on screen.
 */
const DOCUMENT_ORDER: Array<{
  /**
   * How this layer names the document, which is what `documentsDecreasingConfidence` is matched
   * against.
   *
   * **`TIdentityDocumentType` is `@verifime/utils`' own vocabulary**, the same one
   * `DocumentTypeSelection` and `ScanIdentityDocument` use in `@verifime/components`, and that
   * package knows nothing about the person API. Matching on the generated enum instead would tie
   * this table to a contract that can move: the day a member is renamed upstream, the mark silently
   * stops appearing here rather than failing to compile in the file that owns the mapping.
   *
   * Carried beside the builder rather than derived from the row's `documentType`, which is a display
   * label: `'Driver Licence'` is not `'DriverLicence'`, and an other identity document's label is
   * whatever the client typed. Matching on the label would silently flag nothing for a licence and
   * would make an other document's mark depend on client-entered text.
   */
  type: TIdentityDocumentType;
  build: (
    documents: TCustomerDocuments,
  ) => Omit<TKycWithDocumentNamesRow, 'id' | 'decreasesConfidence'> | undefined;
}> = [
  {
    type: 'DriverLicence',
    build: ({ driverLicence }) =>
      driverLicence && {
        documentType: 'Driver Licence',
        expires: driverLicence.licenceExpiry,
        countryOfIssue: driverLicence.licenceCountryOfIssue,
        extractedName: driverLicenceName(driverLicence),
      },
  },

  {
    // Beside the licence: a photo card is the same family of Australian card. A row, and nothing
    // more than a row: it carries no status and no source-check reading, because a photo card is
    // never shown as checked against its issuer.
    type: 'PhotoCard',
    build: ({ photoCard }) =>
      photoCard && {
        // UNAPPROVED DRAFT (verifime-adr#816): to be approved with the stakeholder in the running build
        documentType: 'Photo Card',
        // Optional on the card. Left unset when absent, so the table draws the same empty-expiry
        // presentation it draws for a licence that has none.
        expires: photoCard.photoCardExpiry,
        countryOfIssue: photoCard.photoCardCountryOfIssue,
        extractedName: photoCardName(photoCard),
      },
  },

  {
    type: 'Passport',
    build: ({ passport }) =>
      passport && {
        documentType: 'Passport',
        expires: passport.passportExpiry,
        countryOfIssue: passport.passportCountryOfIssue,
        // A passport folds any middle name into its given names and prints two parts, not three.
        extractedName: structuredOrLegacyName(
          [
            ['Given names', passport.passportGivenNames],
            ['Family name', passport.passportFamilyName],
          ],
          passport.passportName,
        ),
      },
  },

  {
    // Beside the passport: an ImmiCard prints the same two name parts. A row, and nothing more than
    // a row: no status, and no card number, because nothing on this table shows either.
    type: 'ImmiCard',
    build: ({ immiCard }) =>
      immiCard && {
        documentType: IMMI_CARD_COPY.documentType,
        expires: immiCard.immiCardExpiry,
        // No country of issue, and none is invented here: the response has no such field for an
        // ImmiCard.
        extractedName: structuredOrLegacyName(
          [
            [IMMI_CARD_COPY.givenNames, immiCard.immiCardGivenNames],
            [IMMI_CARD_COPY.familyName, immiCard.immiCardFamilyName],
          ],
          immiCard.immiCardName,
        ),
      },
  },

  {
    type: 'MedicareCard',
    build: ({ medicareCard }) =>
      medicareCard && {
        documentType: 'Medicare Card',
        expires: medicareCard.medicareCardExpiry,
        // No country of issue on the card, and none is invented here: a dash belongs to the
        // renderer, not to a value the document does not have.
        extractedName: nameParts([['Name', medicareCard.medicareCardFullName]]),
      },
  },

  {
    type: 'OtherIdentityDocument',
    build: ({ otherIdentityDocument }) =>
      otherIdentityDocument && {
        // The type is whatever the client recorded, since there is no fixed vocabulary for it.
        documentType: otherIdentityDocument.otherDocumentType,
        expires: otherIdentityDocument.dateOfExpiry,
        countryOfIssue: otherIdentityDocument.countryOfIssue,
        // This response carries no name fields, so there is nothing to show. Inventing a single
        // empty "Name" part would put a blank in a column that otherwise always has a value.
      },
  },
];

/**
 * Turns a customer's identity documents into the rows the KYC table renders, one per document.
 *
 * **Lives in the views layer, not the component.** The structured name fields and the whole-name
 * fallback are a concept from the API's own document shapes, and turning that into the row shape a
 * presentational table needs is a views concern (ADR 50/62): the component downstream takes
 * `TKycWithDocumentNamesRow[]` and knows nothing about a licence or a passport. `documentRows` is
 * exported from this package for the same reason it was worth exporting before: it is the part
 * worth reusing and worth testing on its own, decoupled from rendering.
 *
 * The row ids are positional and stable for a given set of documents, which is all the grid needs:
 * it keys on them and nothing else refers to them.
 *
 * **`lastUpdatedOn` is the customer's, not the document's**, and it lands on every row identically.
 * That is what the record actually holds: the customer response carries one `lastUpdatedDate` and
 * the individual document objects carry no timestamp of their own. The old table populates the
 * column the same way, and the prototype shows the same date against every row. Taking it as
 * a parameter rather than reaching for it here keeps this function pure over its input, and makes
 * a caller that has no timestamp render a dash rather than a wrong date.
 *
 * **`documentsDecreasingConfidence` names the documents behind a low document confidence level**,
 * in this layer's own vocabulary rather than the API definition's. Translating the confidence
 * response into these names is a separate concern from building the rows: a caller derives the list
 * from whatever confidence response it holds and passes it in here, this function does not fetch or
 * derive it.
 *
 * A list of document types and nothing else, which is what keeps the mark unable to say more than
 * which documents: a list of types cannot carry a reason by construction, and we are not permitted
 * to tell a client which government check failed.
 *
 * An omitted list resolves to `false` on every row, exactly as an empty one does. Naming a type the
 * customer does not hold flags nothing: the list decorates rows that already exist and never adds
 * one.
 */
export function documentRows(
  documents: TCustomerDocuments,
  lastUpdatedOn?: string,
  documentsDecreasingConfidence?: TIdentityDocumentType[],
): TKycWithDocumentNamesRow[] {
  return DOCUMENT_ORDER.map(({ type, build }) => {
    const row = build(documents);

    return (
      row && {
        ...row,
        decreasesConfidence: Boolean(documentsDecreasingConfidence?.includes(type)),
      }
    );
  })
    .filter(Boolean)
    .map((row, index) => ({ id: index + 1, lastUpdated: lastUpdatedOn, ...row }));
}
